import React, { Component } from "react";
import { observer, inject } from "mobx-react";
import { withRouter } from "react-router-dom";
import ModalContainer from "../../components/modal/ModalContainer";
import {
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    ExpansionPanel,
    ExpansionPanelSummary,
    ExpansionPanelDetails,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Divider
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import WarningIcon from "@material-ui/icons/Warning";
import moment from "moment";
import currencyHelper from "../../helper/currency";
import agent from "../../agent";
import { SuperLedgerStatus } from "../../types/enum";

// Temporary: assume HG Super Pay add-on is active until live billing integration is ready
const ASSUME_ADDON_ACTIVE = true;
const MAX_HG_SUPER_PAY_PAYEES = 5;
const PAYEE_LIMIT_MESSAGE = `You can select a maximum of ${MAX_HG_SUPER_PAY_PAYEES} payees per payment`;

// Failed ledgers are retryable — treat them the same as Pending for selection purposes
const isRetryableItem = (item) =>
    item.status === SuperLedgerStatus.Pending.ordinal ||
    item.status === SuperLedgerStatus.Failed.ordinal ||
    item.status === SuperLedgerStatus.Refunded.ordinal;

// A ledger with a zero super fee cannot be meaningfully paid
const isZeroFee = (item) => parseFloat(item.superFee || 0) === 0;

// An item is selectable: retryable status AND non-zero fee
const isSelectableItem = (item) => isRetryableItem(item) && !isZeroFee(item);

@inject("superLedgerStore", "templateStore", "hgSuperPayStore", "profileStore", "xeroStore", "hgSuperPayAddonStore")
@withRouter
@observer
class MultiPayeePaymentModal extends Component {
    state = {
        selectedPayeeIds: new Set(),
        selectedItemIds: new Set(),
        expandedPayees: new Set(),
        isProcessing: false,
        paymentResults: null,
        failedPayeeStatus: {},
        isValidating: false,
        validationDone: false,
        validatedPayeeIds: new Set(),
        invalidPayeeReasons: {},
        ledgerValidation: null,
        submittedPaymentId: null,
        submittedReference: null,
        submittedSuccess: null,
        isCheckingStatus: false,
        paymentStatusResult: null
    };

    componentDidUpdate(prevProps) {
        if (this.props.open && !prevProps.open) {
            const payees = this.props.payees || [];
            const eligiblePayees = payees.filter(payee =>
                (payee.items || []).some(item => isSelectableItem(item))
            );
            const initiallySelectedPayees = this.props.isHgSuperPay
                ? eligiblePayees.slice(0, MAX_HG_SUPER_PAY_PAYEES)
                : payees;
            const selectedPayeeIds = new Set(initiallySelectedPayees.map(p => p.payeeId));
            const selectedItemIds = new Set();
            const expandedPayees = new Set();
            
            initiallySelectedPayees.forEach(payee => {
                (payee.items || []).forEach(item => {
                    if (isSelectableItem(item)) {
                        selectedItemIds.add(item.id);
                    }
                });
            });
            
            this.setState({
                selectedPayeeIds,
                selectedItemIds,
                expandedPayees,
                paymentResults: null,
                failedPayeeStatus: {},
                isValidating: false,
                validationDone: false,
                validatedPayeeIds: new Set(),
                invalidPayeeReasons: {},
                ledgerValidation: null,
                submittedPaymentId: null,
                submittedReference: null,
                submittedSuccess: null,
                isCheckingStatus: false,
                paymentStatusResult: null
            });

            if (this.props.isHgSuperPay) {
                this.props.hgSuperPayAddonStore.loadAddonStatus();
            }
        }
    }

    handlePayeeToggle = (payeeId) => {
        const { selectedPayeeIds, selectedItemIds } = this.state;
        const payee = this.props.payees.find(p => p.payeeId === payeeId);
        const newSelectedPayeeIds = new Set(selectedPayeeIds);
        const newSelectedItemIds = new Set(selectedItemIds);

        if (newSelectedPayeeIds.has(payeeId)) {
            newSelectedPayeeIds.delete(payeeId);
            (payee?.items || []).forEach(item => {
                newSelectedItemIds.delete(item.id);
            });
        } else {
            if (this.props.isHgSuperPay && newSelectedPayeeIds.size >= MAX_HG_SUPER_PAY_PAYEES) {
                this.props.templateStore.openSnackbar(PAYEE_LIMIT_MESSAGE);
                return;
            }
            newSelectedPayeeIds.add(payeeId);
            (payee?.items || []).forEach(item => {
                if (isSelectableItem(item)) {
                    newSelectedItemIds.add(item.id);
                }
            });
        }

        this.setState({
            selectedPayeeIds: newSelectedPayeeIds,
            selectedItemIds: newSelectedItemIds,
            validationDone: false,
            validatedPayeeIds: new Set(),
            invalidPayeeReasons: {},
            ledgerValidation: null
        });
    };

    handleItemToggle = (itemId, payeeId) => {
        const { selectedItemIds, selectedPayeeIds } = this.state;
        const payee = this.props.payees.find(p => p.payeeId === payeeId);
        const newSelectedItemIds = new Set(selectedItemIds);
        const newSelectedPayeeIds = new Set(selectedPayeeIds);

        if (newSelectedItemIds.has(itemId)) {
            newSelectedItemIds.delete(itemId);
        } else {
            if (this.props.isHgSuperPay &&
                !newSelectedPayeeIds.has(payeeId) &&
                newSelectedPayeeIds.size >= MAX_HG_SUPER_PAY_PAYEES) {
                this.props.templateStore.openSnackbar(PAYEE_LIMIT_MESSAGE);
                return;
            }
            newSelectedItemIds.add(itemId);
        }

        const payeeUnpaidItems = (payee?.items || []).filter(item => isRetryableItem(item));
        const hasSelectedItems = payeeUnpaidItems.some(item => newSelectedItemIds.has(item.id));
        
        if (hasSelectedItems) {
            newSelectedPayeeIds.add(payeeId);
        } else {
            newSelectedPayeeIds.delete(payeeId);
        }

        this.setState({
            selectedItemIds: newSelectedItemIds,
            selectedPayeeIds: newSelectedPayeeIds,
            validationDone: false,
            validatedPayeeIds: new Set(),
            invalidPayeeReasons: {},
            ledgerValidation: null
        });
    };

    handleExpandToggle = (payeeId) => {
        const { expandedPayees } = this.state;
        const newExpandedPayees = new Set(expandedPayees);
        
        if (newExpandedPayees.has(payeeId)) {
            newExpandedPayees.delete(payeeId);
        } else {
            newExpandedPayees.add(payeeId);
        }

        this.setState({ expandedPayees: newExpandedPayees });
    };

    getPayeeName = (payee) => {
        const payeeProfile = payee.payee || {};
        return payeeProfile.profileName 
            ? payeeProfile.profileName 
            : `${payeeProfile.firstName || ''} ${payeeProfile.lastName || ''}`.trim() || 'Unknown';
    };

    calculatePayeeTotal = (payee) => {
        const { selectedItemIds } = this.state;
        let total = 0;
        (payee.items || []).forEach(item => {
            if (selectedItemIds.has(item.id)) {
                total += parseFloat(item.superFee || 0);
            }
        });
        return total;
    };

    calculateGrandTotal = () => {
        const { payees } = this.props;
        const { selectedPayeeIds } = this.state;
        let total = 0;
        
        (payees || []).forEach(payee => {
            if (selectedPayeeIds.has(payee.payeeId)) {
                total += this.calculatePayeeTotal(payee);
            }
        });
        
        return total;
    };

    handleGoToXeroSettings = (payeeId) => {
        const { onClose } = this.props;
        if (onClose) {
            onClose();
        }
        this.props.history.push("/xeroSetting/soleTraderMapping");
    };

    handleCreateEmployeeForPayee = async (payeeId) => {
        const { failedPayeeStatus } = this.state;
        
        const currentProfile = this.props.profileStore.getCurrentProfile();
        if (!currentProfile?.id) {
            this.props.templateStore.openSnackbar("Unable to identify your profile");
            return;
        }

        this.setState({
            failedPayeeStatus: {
                ...failedPayeeStatus,
                [payeeId]: { ...failedPayeeStatus[payeeId], resolving: true }
            }
        });

        try {
            const response = await this.props.xeroStore.createXeroEmployee(currentProfile.id, payeeId);
            
            if (response.status === 401) {
                this.props.templateStore.openSnackbar("You don't have permission for this action");
                this.setState({
                    failedPayeeStatus: {
                        ...this.state.failedPayeeStatus,
                        [payeeId]: { ...this.state.failedPayeeStatus[payeeId], resolving: false, reason: "Permission denied" }
                    }
                });
                return;
            }
            if (response.status === 400) {
                const errorMsg = "Create employee error, please verify super annuation data of the musician. If the issue persists, contact support";
                this.props.templateStore.openSnackbar(errorMsg);
                this.setState({
                    failedPayeeStatus: {
                        ...this.state.failedPayeeStatus,
                        [payeeId]: { ...this.state.failedPayeeStatus[payeeId], resolving: false, reason: errorMsg }
                    }
                });
                return;
            }
            if (response.status === 200) {
                if (response.data.isError) {
                    this.props.templateStore.openSnackbar(response.data.message);
                    this.setState({
                        failedPayeeStatus: {
                            ...this.state.failedPayeeStatus,
                            [payeeId]: { ...this.state.failedPayeeStatus[payeeId], resolving: false, reason: response.data.message }
                        }
                    });
                    return;
                }
                this.setState(prevState => {
                    const newStatus = { ...prevState.failedPayeeStatus };
                    delete newStatus[payeeId];
                    const newResults = { ...prevState.paymentResults };
                    delete newResults[payeeId];
                    return { failedPayeeStatus: newStatus, paymentResults: newResults };
                });
                this.props.templateStore.openSnackbar(`Employee ${response.data.EmployeeID} created`);
                return;
            }
            this.props.templateStore.openSnackbar("An unexpected error occurred");
            this.setState({
                failedPayeeStatus: {
                    ...this.state.failedPayeeStatus,
                    [payeeId]: { ...this.state.failedPayeeStatus[payeeId], resolving: false, reason: "Unexpected error" }
                }
            });
        } catch (error) {
            this.setState({
                failedPayeeStatus: {
                    ...this.state.failedPayeeStatus,
                    [payeeId]: {
                        ...this.state.failedPayeeStatus[payeeId],
                        resolving: false,
                        reason: error?.message || error || "Failed to create employee"
                    }
                }
            });
            this.props.templateStore.openSnackbar(error?.message || "Failed to create employee");
        }
    };

    hasAnyResolvingPayee = () => {
        const { failedPayeeStatus } = this.state;
        return Object.values(failedPayeeStatus).some(status => status.resolving);
    };

    handleValidate = async () => {
        const { selectedPayeeIds, selectedItemIds } = this.state;
        const { payees } = this.props;

        if (selectedItemIds.size === 0) {
            this.props.templateStore.openSnackbar("Please select at least one item to validate");
            return;
        }

        if (selectedPayeeIds.size > MAX_HG_SUPER_PAY_PAYEES) {
            this.props.templateStore.openSnackbar(PAYEE_LIMIT_MESSAGE);
            return;
        }

        this.setState({
            isValidating: true,
            validationDone: false,
            invalidPayeeReasons: {},
            validatedPayeeIds: new Set(),
            ledgerValidation: null
        });

        const payeeIdsList = Array.from(selectedPayeeIds);
        const ledgerIdsList = Array.from(selectedItemIds);

        try {
            const [payeeResults, ledgerResult] = await Promise.all([
                this.props.hgSuperPayStore.validatePayees(payeeIdsList),
                this.props.hgSuperPayStore.validateLedgers(ledgerIdsList)
            ]);

            const payeeArray = Array.isArray(payeeResults) ? payeeResults : (payeeResults?.response || []);
            const ledgerData = ledgerResult?.response || ledgerResult || {};

            const validatedPayeeIds = new Set();
            const invalidPayeeReasons = {};

            payeeArray.forEach(r => {
                if (r.isValidated) {
                    validatedPayeeIds.add(r.payeeId);
                } else {
                    invalidPayeeReasons[r.payeeId] = r.reason || "Validation failed";
                }
            });

            const newSelectedPayeeIds = new Set(selectedPayeeIds);
            const newSelectedItemIds = new Set(selectedItemIds);
            Object.keys(invalidPayeeReasons).forEach(id => {
                const payeeId = parseInt(id, 10);
                newSelectedPayeeIds.delete(payeeId);
                const payee = (payees || []).find(p => p.payeeId === payeeId);
                (payee?.items || []).forEach(item => newSelectedItemIds.delete(item.id));
            });

            this.setState({
                isValidating: false,
                validationDone: true,
                validatedPayeeIds,
                invalidPayeeReasons,
                ledgerValidation: ledgerData,
                selectedPayeeIds: newSelectedPayeeIds,
                selectedItemIds: newSelectedItemIds
            });
        } catch (error) {
            const msg = error?.response?.data?.message || error?.message || "Validation failed";
            this.props.templateStore.openSnackbar(msg);
            this.setState({ isValidating: false });
        }
    };

    handleCheckPaymentStatus = async () => {
        const { submittedPaymentId } = this.state;
        if (!submittedPaymentId) return;

        this.setState({ isCheckingStatus: true, paymentStatusResult: null });

        try {
            const result = await this.props.hgSuperPayStore.syncPaymentStatus(submittedPaymentId);
            this.setState({ paymentStatusResult: result, isCheckingStatus: false });
        } catch (error) {
            this.props.templateStore.openSnackbar("Failed to check payment status");
            this.setState({ isCheckingStatus: false });
        }
    };

    handleProcessPayment = async () => {
        const { selectedPayeeIds, selectedItemIds, validationDone, validatedPayeeIds } = this.state;
        const { payees, onSuccess } = this.props;

        const addonStatus = this.props.hgSuperPayAddonStore.addonStatus;
        if (!ASSUME_ADDON_ACTIVE && addonStatus !== "active") {
            this.props.templateStore.openSnackbar("Please activate the HG Super Pay add-on in Account Settings → Membership before processing payments");
            return;
        }

        if (!validationDone) {
            this.props.templateStore.openSnackbar("Please validate payees before submitting");
            return;
        }

        if (selectedItemIds.size === 0) {
            this.props.templateStore.openSnackbar("Please select at least one item to process");
            return;
        }

        if (selectedPayeeIds.size > MAX_HG_SUPER_PAY_PAYEES) {
            this.props.templateStore.openSnackbar(PAYEE_LIMIT_MESSAGE);
            return;
        }

        this.setState({ isProcessing: true });

        try {
            const currentProfile = this.props.profileStore.getCurrentProfile();
            const createdByProfileId = currentProfile?.id;

            if (!createdByProfileId) {
                this.props.templateStore.openSnackbar("Unable to identify your profile. Please try again.");
                return;
            }

            const payeesPayload = [];
            (payees || []).forEach(payee => {
                if (selectedPayeeIds.has(payee.payeeId) && validatedPayeeIds.has(payee.payeeId)) {
                    const superLedgerIds = (payee.items || [])
                        .filter(item => selectedItemIds.has(item.id))
                        .map(item => item.id);

                    if (superLedgerIds.length > 0) {
                        payeesPayload.push({ payeeId: payee.payeeId, superLedgerIds });
                    }
                }
            });

            if (payeesPayload.length === 0) {
                this.props.templateStore.openSnackbar("No validated payees with items to process");
                return;
            }

            if (new Set(payeesPayload.map(payee => payee.payeeId)).size > MAX_HG_SUPER_PAY_PAYEES) {
                this.props.templateStore.openSnackbar(PAYEE_LIMIT_MESSAGE);
                return;
            }

            // Derive the raw payer ID from the first selected ledger item.
            // Effective-payer resolution is handled inside hgSuperPayStore.submitPayment.
            const firstItem = (payees || []).flatMap(p => p.items || []).find(item => selectedItemIds.has(item.id));
            const payerId = firstItem?.payer?.id || createdByProfileId;

            const requestBody = { payerId, createdByProfileId, payees: payeesPayload };
            const result = await this.props.hgSuperPayStore.submitPayment(requestBody);

            const apiData = result?.response || result?.data?.response || result?.data || result;
            const { success, paymentId, externalReference, results = [], summary } = apiData || {};

            const resultsMap = {};
            results.forEach(r => { resultsMap[r.payeeId] = r; });

            this.setState({
                paymentResults: resultsMap,
                submittedPaymentId: paymentId || null,
                submittedReference: externalReference || null,
                submittedSuccess: success !== false
            });

            if (summary) {
                const { successful, failed } = summary;

                if (failed === 0 && successful > 0) {
                    this.props.templateStore.openSnackbar(`${successful} payment${successful > 1 ? 's' : ''} processed successfully`);
                } else if (successful === 0 && failed > 0) {
                    this.props.templateStore.openSnackbar(`All ${failed} payment${failed > 1 ? 's' : ''} failed`);
                } else if (successful > 0 && failed > 0) {
                    this.props.templateStore.openSnackbar(`${successful} successful, ${failed} failed`);
                }

                const successfulLedgerIds = [];
                (payees || []).forEach(payee => {
                    const r = resultsMap[payee.payeeId];
                    if (r?.status === "success") {
                        (payee.items || []).forEach(item => {
                            if (selectedItemIds.has(item.id)) successfulLedgerIds.push(item.id);
                        });
                    }
                });

                if (successfulLedgerIds.length > 0) {
                    this.props.superLedgerStore.markLedgersAsPaidInStore(successfulLedgerIds);
                }

                if (failed === 0 && onSuccess) onSuccess();
            } else if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            const errorData = error?.response?.data;
            this.props.templateStore.openSnackbar(
                errorData?.message || error?.message || "Failed to process payment"
            );
        } finally {
            this.setState({ isProcessing: false });
        }
    };

    handleSubmitToXero = async () => {
        const { selectedPayeeIds, selectedItemIds } = this.state;
        const { payees, onSuccess, startDate, endDate, filterType } = this.props;
        
        if (selectedItemIds.size === 0) {
            this.props.templateStore.openSnackbar("Please select at least one item to process");
            return;
        }

        const isCustomWeek = filterType === "custom" && startDate && endDate &&
            moment(startDate).isoWeekday() === 1 &&
            moment(endDate).isoWeekday() === 7 &&
            moment(endDate).diff(moment(startDate), "days") === 6;

        const isEligibleFilter = filterType === "week" || filterType === "lastweek" ||
            filterType === "quarterly" || isCustomWeek;

        if (!isEligibleFilter) {
            this.props.templateStore.openSnackbar("Please select a weekly or quarterly filter before submitting to Xero");
            return;
        }

        this.setState({ isProcessing: true });

        try {
            const currentProfile = this.props.profileStore.getCurrentProfile();
            const payerId = currentProfile?.id;

            if (!payerId) {
                this.props.templateStore.openSnackbar("Unable to identify your profile. Please try again.");
                return;
            }

            const payeesPayload = [];
            (payees || []).forEach(payee => {
                if (selectedPayeeIds.has(payee.payeeId)) {
                    const superLedgerIds = (payee.items || [])
                        .filter(item => selectedItemIds.has(item.id))
                        .map(item => item.id);
                    
                    if (superLedgerIds.length > 0) {
                        payeesPayload.push({
                            payeeId: payee.payeeId,
                            superLedgerIds: superLedgerIds
                        });
                    }
                }
            });

            if (payeesPayload.length === 0) {
                this.props.templateStore.openSnackbar("Please select at least one payee with items to process");
                return;
            }

            const requestBody = {
                dateRange: {
                    startDate: moment(startDate).format("YYYY-MM-DD"),
                    endDate: moment(endDate).format("YYYY-MM-DD"),
                    paymentDate: moment().format("YYYY-MM-DD")
                },
                payerId,
                createdByProfileId: payerId,
                payees: payeesPayload
            };

            const result = await agent.Xero.submitPayment(requestBody);
            
            const rawData = result?.data || result;
            const apiData = rawData?.response || rawData;
            
            if (apiData?.success === false) {
                let errorMessage = apiData.error || apiData.message || "Failed to submit payment to Xero";
                let addContactSupport = false;
                
                this.setState({ failedPayeeStatus: {} });
                
                if (apiData.errorType === "LEDGER_DATE_OUT_OF_RANGE" && apiData.invalidLedgers?.length > 0) {
                    const count = apiData.invalidLedgers.length;
                    errorMessage = `${count} ledger${count > 1 ? 's have' : ' has'} booking dates outside the pay run period`;
                    addContactSupport = true;
                } else if (apiData.errorType === "MISSING_XERO_CONFIG") {
                    errorMessage = "Xero configuration is missing. Please check your profile settings.";
                    addContactSupport = true;
                } else if (apiData.errorType === "PAYEE_VALIDATION_FAILED") {
                    const results = apiData.results || [];
                    const failedPayees = results.filter(r => r.status === "failed");
                    const newFailedStatus = {};
                    failedPayees.forEach(p => {
                        const id = p.payeeId || p.id || p;
                        newFailedStatus[id] = {
                            reason: p.reason || p.error || "Not mapped to Xero employee",
                            resolving: false,
                            resolved: false
                        };
                    });
                    errorMessage = "Some payees are not mapped to Xero employees. Use the options below each payee to fix.";
                    this.setState({ failedPayeeStatus: newFailedStatus });
                } else if (apiData.errorType === "XERO_CALENDAR_ERROR") {
                    errorMessage = "Failed to access Xero payroll calendar";
                    addContactSupport = true;
                } else if (apiData.errorType === "XERO_PAYRUN_ERROR") {
                    errorMessage = "Failed to create or access Xero pay run";
                    addContactSupport = true;
                } else if (apiData.errorType === "XERO_PAYSLIP_ERROR") {
                    errorMessage = "Failed to create or update Xero payslips";
                    addContactSupport = true;
                } else if (apiData.errorType) {
                    addContactSupport = true;
                }
                
                if (addContactSupport) {
                    errorMessage += ". Please contact admin support.";
                }
                
                this.props.templateStore.openSnackbar(errorMessage);
                
                const results = apiData?.results || [];
                if (results.length > 0) {
                    const resultsMap = {};
                    results.forEach(r => {
                        resultsMap[r.payeeId] = {
                            ...r,
                            status: r.status === "failed" || r.status === "skipped" ? "failed" : r.status,
                            error: r.error || r.reason
                        };
                    });
                    this.setState({ paymentResults: resultsMap });
                }
                return;
            }

            const summary = apiData?.summary;
            const results = apiData?.results || [];

            const resultsMap = {};
            results.forEach(r => {
                const isSuccess = r.status === "created" || r.status === "updated";
                resultsMap[r.payeeId] = {
                    ...r,
                    status: isSuccess ? "success" : "failed",
                    error: r.error || r.reason
                };
            });
            this.setState({ paymentResults: resultsMap, failedPayeeStatus: {} });

            if (summary) {
                const { successful, failed, skipped } = summary;
                
                let message = "";
                if (failed === 0 && successful > 0) {
                    message = `${successful} payment${successful > 1 ? 's' : ''} submitted to Xero successfully`;
                } else if (successful === 0 && failed > 0) {
                    message = `All ${failed} payment${failed > 1 ? 's' : ''} failed`;
                } else if (successful > 0 && failed > 0) {
                    message = `${successful} successful, ${failed} failed`;
                }
                if (skipped > 0) {
                    message += ` (${skipped} skipped)`;
                }
                
                if (message) {
                    this.props.templateStore.openSnackbar(message);
                }

                const successfulPayeeIds = results
                    .filter(r => r.status === "created" || r.status === "updated")
                    .map(r => r.payeeId);

                if (successfulPayeeIds.length > 0) {
                    const successfulLedgerIds = [];
                    (payees || []).forEach(payee => {
                        if (successfulPayeeIds.includes(payee.payeeId)) {
                            (payee.items || []).forEach(item => {
                                if (selectedItemIds.has(item.id)) {
                                    successfulLedgerIds.push(item.id);
                                }
                            });
                        }
                    });
                    
                    if (successfulLedgerIds.length > 0) {
                        this.props.superLedgerStore.markLedgersAsPaidInStore(successfulLedgerIds);
                    }
                }

                if (failed === 0 && onSuccess) {
                    onSuccess();
                }
            } else if (apiData?.success === true && onSuccess) {
                onSuccess();
            }
        } catch (error) {
            const errorData = error?.response?.data;
            let errorMessage = "Failed to submit payment to Xero";
            
            if (errorData?.errorType) {
                if (errorData.errorType === "INVALID_DATE_FORMAT") {
                    errorMessage = "Invalid date format. Please use YYYY-MM-DD format.";
                } else if (errorData.errorType === "DUPLICATE_LEDGER") {
                    errorMessage = "Duplicate ledger entries detected in request";
                } else if (errorData.errorType === "PAYER_NOT_FOUND") {
                    errorMessage = "Payer profile not found";
                } else if (errorData.error) {
                    errorMessage = errorData.error;
                } else if (errorData.message) {
                    errorMessage = errorData.message;
                }
            } else if (errorData?.error) {
                errorMessage = errorData.error;
            } else if (errorData?.message) {
                errorMessage = errorData.message;
            }
            
            this.props.templateStore.openSnackbar(errorMessage);
        } finally {
            this.setState({ isProcessing: false });
        }
    };

    render() {
        const { open, onClose, payees, currencySymbol, title, submitLabel, isHgSuperPay } = this.props;
        const {
            selectedPayeeIds, selectedItemIds, expandedPayees, isProcessing, paymentResults, failedPayeeStatus,
            isValidating, validationDone, validatedPayeeIds, invalidPayeeReasons, ledgerValidation,
            submittedPaymentId, submittedReference, submittedSuccess, isCheckingStatus, paymentStatusResult
        } = this.state;
        const hasResolvingPayee = this.hasAnyResolvingPayee();
        const grandTotal = this.calculateGrandTotal();
        const addonStatus = this.props.hgSuperPayAddonStore.addonStatus;
        const addonNotActive = !ASSUME_ADDON_ACTIVE && isHgSuperPay && addonStatus !== "active" && addonStatus !== "loading";
        const allPayeesValidated = validationDone &&
            selectedPayeeIds.size > 0 &&
            Array.from(selectedPayeeIds).every(payeeId => validatedPayeeIds.has(payeeId));
        const overallValid = validationDone && !!ledgerValidation?.valid && allPayeesValidated;
        const canSubmitHgPay = validationDone && validatedPayeeIds.size > 0 && overallValid;

        return (
            <ModalContainer 
                open={open} 
                onClose={onClose} 
                title={title || "Send to XERO"}
                coloredDialogTitle={true}
                maxWidth="md"
                fullWidth
            >
                <DialogContent style={{ minHeight: 300 }}>
                    {addonNotActive && (
                        <div style={{
                            padding: "14px 16px",
                            marginBottom: 16,
                            background: "#fff8fd",
                            border: "1px solid #fce4f7",
                            borderRadius: 8,
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 10
                        }}>
                            <div>
                                <Typography variant="body2" style={{ color: "#c62828", fontWeight: 600, marginBottom: 4 }}>
                                    HG Super Pay add-on required
                                </Typography>
                                <Typography variant="body2" style={{ color: "#555" }}>
                                    To use HG Super Pay, please activate the HG Super Pay add-on in{" "}
                                    <a href="/account/setting/membership" style={{ color: "#fe0d9d", fontWeight: 600 }}>
                                        Account Settings → Membership
                                    </a>
                                    .
                                </Typography>
                            </div>
                        </div>
                    )}
                    <Typography variant="subtitle1" style={{ marginBottom: 16 }}>
                        Select payees and items to include in this payment:
                    </Typography>
                    
                    {(payees || []).map(payee => {
                        const payeeId = payee.payeeId;
                        const isPayeeSelected = selectedPayeeIds.has(payeeId);
                        const isExpanded = expandedPayees.has(payeeId);
                        const payeeName = this.getPayeeName(payee);
                        const payeeTotal = this.calculatePayeeTotal(payee);
                        const superProfile = payee.payee?.superAnnuationProfile || {};
                        const items = payee.items || [];
                        const firstItemPayer = items[0]?.payer;
                        const payerName = firstItemPayer
                            ? (firstItemPayer.profileName || `${firstItemPayer.firstName || ''} ${firstItemPayer.lastName || ''}`.trim() || null)
                            : null;
                        const unpaidItems = items.filter(item => isRetryableItem(item));
                        const selectableItems = unpaidItems.filter(item => !isZeroFee(item));
                        const allZeroFee = unpaidItems.length > 0 && selectableItems.length === 0;
                        const selectedCount = selectableItems.filter(item => selectedItemIds.has(item.id)).length;
                        const payeeResult = paymentResults ? paymentResults[payeeId] : null;
                        const payeeFailedStatus = failedPayeeStatus[payeeId];
                        const isPayeeFailed = !!payeeFailedStatus && !payeeFailedStatus.resolved;
                        const isPayeeResolving = payeeFailedStatus?.resolving;

                        return (
                            <ExpansionPanel 
                                key={payeeId}
                                expanded={isExpanded}
                                onChange={() => this.handleExpandToggle(payeeId)}
                                style={{ marginBottom: 8, border: isPayeeFailed ? "1px solid #f44336" : undefined }}
                            >
                                <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                                    <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                                        <Checkbox
                                            checked={isPayeeSelected}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                this.handlePayeeToggle(payeeId);
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            color="primary"
                                            disabled={allZeroFee}
                                        />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                                                    {payeeName}
                                                </Typography>
                                                {allZeroFee && (
                                                    <WarningIcon style={{ color: "#f57c00", fontSize: 18 }} />
                                                )}
                                                {payeeResult && payeeResult.status === "success" && (
                                                    <CheckCircleIcon style={{ color: "#4CAF50", fontSize: 18 }} />
                                                )}
                                                {payeeResult && payeeResult.status === "failed" && (
                                                    <ErrorIcon style={{ color: "#f44336", fontSize: 18 }} />
                                                )}
                                                {isPayeeFailed && !isPayeeResolving && (
                                                    <ErrorIcon style={{ color: "#f44336", fontSize: 18 }} />
                                                )}
                                            </div>
                                            <Typography variant="caption" color="textSecondary">
                                                {selectedCount}/{selectableItems.length} items selected |
                                                Total: {currencySymbol}{currencyHelper.formatMoney(payeeTotal, 2)}
                                                {payerName ? ` | Payer: ${payerName}` : ""}
                                            </Typography>
                                            {payeeResult && payeeResult.status === "failed" && payeeResult.error && (
                                                <Typography variant="caption" style={{ color: "#f44336", display: "block" }}>
                                                    {payeeResult.error}
                                                </Typography>
                                            )}
                                        </div>
                                    </div>
                                </ExpansionPanelSummary>
                                <ExpansionPanelDetails style={{ flexDirection: "column" }}>
                                    {isPayeeFailed && (
                                        <div style={{ 
                                            marginBottom: 16, 
                                            padding: "12px 16px", 
                                            backgroundColor: "#ffebee", 
                                            borderRadius: 4,
                                            border: "1px solid #ffcdd2"
                                        }}>
                                            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
                                                <ErrorIcon style={{ color: "#f44336", fontSize: 20, marginTop: 2 }} />
                                                <div>
                                                    <Typography variant="body2" style={{ color: "#c62828", fontWeight: 500 }}>
                                                        Not mapped to Xero
                                                    </Typography>
                                                    <Typography variant="caption" style={{ color: "#c62828" }}>
                                                        {payeeFailedStatus.reason}
                                                    </Typography>
                                                </div>
                                            </div>
                                            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={() => this.handleGoToXeroSettings(payeeId)}
                                                    disabled={isPayeeResolving}
                                                >
                                                    Map to Existing
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => this.handleCreateEmployeeForPayee(payeeId)}
                                                    disabled={isPayeeResolving}
                                                >
                                                    {isPayeeResolving ? "Creating..." : "Create New Employee"}
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                    {isHgSuperPay && validationDone && invalidPayeeReasons[payeeId] && (
                                        <div style={{
                                            marginBottom: 16,
                                            padding: "12px 16px",
                                            backgroundColor: "#ffebee",
                                            borderRadius: 4,
                                            border: "1px solid #ffcdd2"
                                        }}>
                                            <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                                                <ErrorIcon style={{ color: "#f44336", fontSize: 20, marginTop: 2 }} />
                                                <div>
                                                    <Typography variant="body2" style={{ color: "#c62828", fontWeight: 500 }}>
                                                        Validation Failed
                                                    </Typography>
                                                    <Typography variant="caption" style={{ color: "#c62828" }}>
                                                        {invalidPayeeReasons[payeeId]}
                                                    </Typography>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {isHgSuperPay && validationDone && validatedPayeeIds.has(payeeId) && (
                                        <div style={{
                                            marginBottom: 16,
                                            padding: "8px 16px",
                                            backgroundColor: "#e8f5e9",
                                            borderRadius: 4,
                                            border: "1px solid #c8e6c9",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8
                                        }}>
                                            <CheckCircleIcon style={{ color: "#4CAF50", fontSize: 18 }} />
                                            <Typography variant="body2" style={{ color: "#2e7d32" }}>
                                                Validated — ready to submit
                                            </Typography>
                                        </div>
                                    )}
                                    {allZeroFee && (
                                        <div style={{
                                            marginBottom: 16,
                                            padding: "10px 16px",
                                            backgroundColor: "#fff8e1",
                                            borderRadius: 4,
                                            border: "1px solid #ffe082",
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 10
                                        }}>
                                            <WarningIcon style={{ color: "#f57c00", fontSize: 20, flexShrink: 0, marginTop: 1 }} />
                                            <div>
                                                <Typography variant="body2" style={{ color: "#e65100", fontWeight: 600 }}>
                                                    No payable super fee
                                                </Typography>
                                                <Typography variant="caption" style={{ color: "#795548" }}>
                                                    All ledger items for this employee have a $0.00 super fee and cannot be included in a payment. Check the booking fee details to confirm super was calculated correctly.
                                                </Typography>
                                            </div>
                                        </div>
                                    )}
                                    <div style={{ marginBottom: 16, padding: "8px 16px", backgroundColor: "#f5f5f5", borderRadius: 4 }}>
                                        <Typography variant="body2">
                                            <strong>Super Fund:</strong> {superProfile.fundName || 'N/A'} - {superProfile.productName || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>ABN:</strong> {superProfile.aBN || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>USI:</strong> {superProfile.uSI || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Member Number:</strong> {superProfile.superFundMemberNumber || 'N/A'}
                                        </Typography>
                                    </div>
                                    
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding="checkbox"></TableCell>
                                                <TableCell>Date</TableCell>
                                                <TableCell>Description</TableCell>
                                                <TableCell align="right">Super Fee</TableCell>
                                                <TableCell>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {items.map(item => {
                                                const isPaid = !isRetryableItem(item);
                                                const itemZeroFee = !isPaid && isZeroFee(item);
                                                const isDisabled = isPaid || itemZeroFee;
                                                const isSelected = selectedItemIds.has(item.id);
                                                
                                                return (
                                                    <TableRow 
                                                        key={item.id}
                                                        style={{ 
                                                            opacity: isPaid ? 0.5 : 1,
                                                            backgroundColor: isPaid ? "#f9f9f9" : itemZeroFee ? "#fffde7" : "inherit"
                                                        }}
                                                    >
                                                        <TableCell padding="checkbox">
                                                            <Checkbox
                                                                checked={isSelected}
                                                                onChange={() => this.handleItemToggle(item.id, payeeId)}
                                                                disabled={isDisabled}
                                                                color="primary"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {item.booking?.dateStart && moment.utc(item.booking.dateStart).isValid()
                                                                ? moment.utc(item.booking.dateStart).format("DD MMM YYYY")
                                                                : '-'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {item.booking?.title || '-'}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                                                {itemZeroFee && (
                                                                    <WarningIcon style={{ color: "#f57c00", fontSize: 14 }} />
                                                                )}
                                                                {currencySymbol}{currencyHelper.formatMoney(parseFloat(item.superFee || 0), 2)}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell>
                                                            {itemZeroFee ? (
                                                                <Typography variant="caption" style={{ color: "#f57c00", fontStyle: "italic" }}>
                                                                    Not payable
                                                                </Typography>
                                                            ) : item.status === SuperLedgerStatus.Pending.ordinal ? (
                                                                <Typography variant="caption" style={{ color: "#ff9800" }}>
                                                                    Pending
                                                                </Typography>
                                                            ) : item.status === SuperLedgerStatus.Paid.ordinal ? (
                                                                <Typography variant="caption" style={{ color: "#4CAF50" }}>
                                                                    Paid
                                                                </Typography>
                                                            ) : item.status === SuperLedgerStatus.Posted.ordinal ? (
                                                                <Typography variant="caption" style={{ color: "#4CAF50" }}>
                                                                    Posted
                                                                </Typography>
                                                            ) : item.status === SuperLedgerStatus.Processing.ordinal ? (
                                                                <Typography variant="caption" style={{ color: "#2196F3" }}>
                                                                    Processing
                                                                </Typography>
                                                            ) : item.status === SuperLedgerStatus.Failed.ordinal ? (
                                                                <Typography variant="caption" style={{ color: "#f44336", fontWeight: 600 }}>
                                                                    Failed — Retry
                                                                </Typography>
                                                            ) : item.status === SuperLedgerStatus.Refunded.ordinal ? (
                                                                <Typography variant="caption" style={{ color: "#FF9800" }}>
                                                                    Refunded
                                                                </Typography>
                                                            ) : (
                                                                <Typography variant="caption" style={{ color: "#9e9e9e" }}>
                                                                    Unknown
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </ExpansionPanelDetails>
                            </ExpansionPanel>
                        );
                    })}
                    
                    <Divider style={{ margin: "24px 0 16px" }} />

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="h6">
                            Grand Total:
                        </Typography>
                        <Typography variant="h6" style={{ fontWeight: 600 }}>
                            {currencySymbol}{currencyHelper.formatMoney(grandTotal, 2)}
                        </Typography>
                    </div>

                    {grandTotal === 0 && selectedItemIds.size > 0 && (
                        <div style={{
                            marginTop: 12,
                            padding: "10px 16px",
                            backgroundColor: "#fff8e1",
                            borderRadius: 6,
                            border: "1px solid #ffe082",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 10
                        }}>
                            <WarningIcon style={{ color: "#f57c00", fontSize: 20, flexShrink: 0, marginTop: 1 }} />
                            <Typography variant="body2" style={{ color: "#e65100" }}>
                                The grand total is <strong>$0.00</strong>. All selected ledger items have a zero super fee — there is nothing to pay. Deselect them or check the booking fee details to confirm super was calculated correctly.
                            </Typography>
                        </div>
                    )}

                    {isHgSuperPay && validationDone && (
                        <div style={{
                            marginTop: 16,
                            padding: "12px 16px",
                            backgroundColor: overallValid ? "#e8f5e9" : "#ffebee",
                            borderRadius: 6,
                            border: `1px solid ${overallValid ? "#c8e6c9" : "#ffcdd2"}`
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                                {overallValid
                                    ? <CheckCircleIcon style={{ color: "#4CAF50", fontSize: 18 }} />
                                    : <ErrorIcon style={{ color: "#f44336", fontSize: 18 }} />
                                }
                                <Typography variant="body2" style={{ fontWeight: 600, color: overallValid ? "#2e7d32" : "#c62828" }}>
                                    {overallValid
                                        ? "Ledgers valid"
                                        : !ledgerValidation?.valid
                                            ? "Ledger validation failed"
                                            : "Not all payees validated"}
                                </Typography>
                            </div>
                            {!ledgerValidation?.valid && (ledgerValidation?.payerErrors || []).length > 0 && (
                                <div style={{ marginBottom: 4 }}>
                                    {(ledgerValidation.payerErrors).map((e, i) => (
                                        <Typography key={i} variant="caption" style={{ color: "#c62828", display: "block" }}>
                                            {e.error}
                                        </Typography>
                                    ))}
                                </div>
                            )}
                            {ledgerValidation?.valid && (
                                <Typography variant="caption" style={{ color: "#555" }}>
                                    {ledgerValidation.ledgerCount} ledger{ledgerValidation.ledgerCount !== 1 ? 's' : ''} · Total: {currencySymbol}{currencyHelper.formatMoney(ledgerValidation.totalAmount || 0, 2)}
                                </Typography>
                            )}
                            <Typography variant="caption" style={{ color: "#555", display: "block", marginTop: 4 }}>
                                {validatedPayeeIds.size} of {selectedPayeeIds.size} selected payee{selectedPayeeIds.size !== 1 ? 's' : ''} validated
                            </Typography>
                        </div>
                    )}

                    {isHgSuperPay && submittedPaymentId && (
                        <div style={{
                            marginTop: 16,
                            borderRadius: 6,
                            overflow: "hidden",
                            border: `1px solid ${submittedSuccess ? "#4CAF50" : "#f44336"}`
                        }}>
                            <div style={{ background: submittedSuccess ? "#4CAF50" : "#f44336", padding: "10px 16px" }}>
                                <Typography style={{ color: "#fff", fontWeight: 700 }}>
                                    {submittedSuccess ? "Payment Submitted" : "Payment Failed"}
                                </Typography>
                            </div>
                            <div style={{ padding: "12px 16px" }}>
                                <Typography variant="body2"><strong>Payment ID:</strong> {submittedPaymentId}</Typography>
                                {submittedReference && (
                                    <Typography variant="body2"><strong>Reference:</strong> {submittedReference}</Typography>
                                )}
                                {paymentResults && Object.values(paymentResults).some(r => r.status === "failed") && (
                                    <div style={{ marginTop: 10 }}>
                                        {Object.values(paymentResults).filter(r => r.status === "failed").map(r => (
                                            <Typography key={r.payeeId} variant="body2" style={{ color: "#f44336", marginBottom: 4 }}>
                                                Payee {r.payeeId}: {r.error || "Failed"}
                                            </Typography>
                                        ))}
                                    </div>
                                )}
                                {submittedSuccess && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        onClick={this.handleCheckPaymentStatus}
                                        disabled={isCheckingStatus}
                                        style={{ marginTop: 10 }}
                                    >
                                        {isCheckingStatus ? "Checking..." : "Check Status"}
                                    </Button>
                                )}
                                {paymentStatusResult && (
                                    <div style={{ marginTop: 10 }}>
                                        <Typography variant="body2">
                                            <strong>Status:</strong> {paymentStatusResult.newStatus || paymentStatusResult.status || "Unknown"}
                                        </Typography>
                                        {paymentStatusResult.funds && paymentStatusResult.funds.length > 0 && (
                                            <Typography variant="caption" style={{ color: "#555" }}>
                                                {paymentStatusResult.funds.length} fund{paymentStatusResult.funds.length !== 1 ? 's' : ''} tracked
                                            </Typography>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
                <DialogActions style={{ paddingRight: 24, paddingBottom: 16 }}>
                    <Button onClick={onClose} disabled={isProcessing || hasResolvingPayee || isValidating}>
                        Close
                    </Button>
                    {isHgSuperPay ? (
                        <>
                            <Button
                                variant="outlined"
                                color="primary"
                                style={{ marginLeft: 8 }}
                                onClick={this.handleValidate}
                                disabled={isValidating || isProcessing || selectedItemIds.size === 0 || addonNotActive}
                            >
                                {isValidating ? "Validating..." : validationDone ? "Re-validate" : "Validate"}
                            </Button>
                            <Button
                                variant="contained"
                                style={{ marginLeft: 8 }}
                                color="primary"
                                onClick={this.handleProcessPayment}
                                disabled={isProcessing || isValidating || !canSubmitHgPay || addonNotActive}
                            >
                                {isProcessing ? "Processing..." : (submitLabel || "Pay Now")}
                            </Button>
                        </>
                    ) : (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={this.handleSubmitToXero}
                            disabled={isProcessing || hasResolvingPayee || selectedItemIds.size === 0}
                        >
                            {isProcessing ? "Processing..." : (submitLabel || "Submit to Xero")}
                        </Button>
                    )}
                </DialogActions>
            </ModalContainer>
        );
    }
}

export default MultiPayeePaymentModal;
